// ── LO SCHEMA DI UN ESERCIZIO ────────────────────────────────────────
// Il disegno di un esercizio, come nelle schede del maestro: il campo, le
// sagome dei giocatori, il maestro, i cinesini, le zone e le frecce della
// palla e degli spostamenti (#167).
//
// Si salva come POSIZIONI IN METRI sul campo, non come immagine: pesa poco,
// funziona senza rete, viaggia con l'esercizio nel quaderno, e si disegna
// alla vista che serve — in prospettiva come le schede, o dall'alto. Lo
// schema è uno, le viste due: comporre in una e guardare nell'altra è la
// stessa cosa.
//
// Le coordinate partono dal centro della rete: `x` va da sinistra a destra,
// `y` dalla rete verso chi guarda (la metà di sotto è quella vicina).
// ──────────────────────────────────────────────────────────────────────────

// Tutto quello dove si può mettere qualcosa: il campo blu, largo ben oltre
// le righe come un campo vero, e il verde che gli fa solo da cornice
export const FIELD = { width: 16.97, length: 32.77 }

// Il blu: arriva fin quasi al bordo, e il verde resta una striscia
// sottile. I paletti della rete (COURT.post) ci stanno dentro.
export const SURFACE = { x: FIELD.width / 2 - 0.7, y: FIELD.length / 2 - 0.9 }

// Gli angoli tondi, come le schede e i bottoni dell'app: il raggio del blu,
// e il verde che lo segue largo quanto è larga la cornice, perché resti
// sottile uguale anche negli angoli
export const SURFACE_RADIUS = 1.8
export const FIELD_RADIUS = SURFACE_RADIUS + 0.8

// Le righe di un campo vero, in metri dal centro
export const COURT = {
	doubles: 5.485, // le laterali del doppio
	singles: 4.115, // le laterali del singolo
	baseline: 11.885, // le righe di fondo
	service: 6.4, // le righe del servizio
	post: 6.4, // i paletti della rete
}

// I colori delle sagome, quelli delle schede: il nome è anche quello del
// colore in `theme.colors` (`schema_red`, …)
export const PLAYER_COLORS = [
	{ id: "red", label: "Rosso" },
	{ id: "orange", label: "Arancione" },
	{ id: "teal", label: "Verde acqua" },
	{ id: "violet", label: "Viola" },
	{ id: "yellow", label: "Giallo" },
	{ id: "brown", label: "Marrone" },
]
const COLOR_IDS = PLAYER_COLORS.map(color => color.id)

export const PIECE_TYPES = ["player", "coach", "cone", "number"]

// La palla tonda nasce piegata così: quanto il punto di controllo della
// curva si scosta dalla freccia, in proporzione alla sua lunghezza. Poco:
// una palla alta da fondo a fondo, non mezzo giro, come le disegna il
// maestro; il pallino a metà la piega quanto vuole
export const ROUND_BEND = 0.2
export const MAX_BEND = 1.2

// Un numero che vale per i pezzi, la sagoma (l'ordine in cui si muove) e
// i numeri da soli
export const MAX_NUMBER = 9

// Più di così su un campo non si legge più niente, e un documento
// Firestore resta piccolo
export const MAX_SCHEMA_ITEMS = 40

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
// il centimetro basta, e lo schema salvato resta corto
const round = value => Math.round(value * 100) / 100
const isNumber = value => typeof value === "number" && Number.isFinite(value)

// Un punto dentro il verde. Un po' di margine in fondo: la sagoma è alta, e
// con i piedi sul bordo la testa uscirebbe.
export const keepOnField = ({ x, y }) => ({
	x: round(clamp(x, -FIELD.width / 2 + 0.3, FIELD.width / 2 - 0.3)),
	y: round(clamp(y, -FIELD.length / 2 + 0.6, FIELD.length / 2 - 0.2)),
})

const point = raw => (isNumber(raw?.x) && isNumber(raw?.y) ? keepOnField(raw) : null)

const normalizeItem = raw => {
	if (!raw || typeof raw !== "object") return null
	if (raw.type === "arrow") {
		const a = point(raw.a)
		const b = point(raw.b)
		if (!a || !b) return null
		return {
			type: "arrow",
			kind: raw.kind === "spostamento" ? "spostamento" : "palla",
			a,
			b,
			bend: isNumber(raw.bend) ? round(clamp(raw.bend, -MAX_BEND, MAX_BEND)) : 0,
			// la doppia freccia: un palleggio, o chi va e torna al punto da cui
			// è partito, in una riga sola invece di due affiancate
			both: raw.both === true,
		}
	}
	if (raw.type === "zone") {
		const from = point({ x: raw.x1, y: raw.y1 })
		const to = point({ x: raw.x2, y: raw.y2 })
		if (!from || !to) return null
		return { type: "zone", x1: from.x, y1: from.y, x2: to.x, y2: to.y }
	}
	if (!PIECE_TYPES.includes(raw.type)) return null
	const at = point(raw)
	if (!at) return null
	if (raw.type === "player")
		return {
			type: "player",
			...at,
			color: COLOR_IDS.includes(raw.color) ? raw.color : COLOR_IDS[0],
			ghost: raw.ghost === true,
			num: Number.isInteger(raw.num) ? clamp(raw.num, 0, MAX_NUMBER) : 0,
		}
	if (raw.type === "number")
		return {
			type: "number",
			...at,
			n: Number.isInteger(raw.n) ? clamp(raw.n, 1, MAX_NUMBER) : 1,
		}
	// un cono alto o, se no, una coppella
	if (raw.type === "cone") return { type: "cone", ...at, tall: raw.tall === true }
	return { type: raw.type, ...at }
}

// Lo schema com'è salvato, o com'è nell'editor, ridotto a quello che vale:
// pezzi conosciuti, dentro il campo, senza le chiavi dell'editor. Uno
// schema senza niente sopra non è uno schema: `null`.
export const normalizeSchema = raw => {
	const items = (Array.isArray(raw?.items) ? raw.items : [])
		.map(normalizeItem)
		.filter(Boolean)
		.slice(0, MAX_SCHEMA_ITEMS)
	return items.length > 0 ? { items } : null
}

// Un'impronta corta dello schema, per dire se è ancora quello del quaderno
// senza tenerne una seconda copia nella lezione. Due schemi uguali hanno la
// stessa; nessuno schema, la stringa vuota.
export const schemaSignature = schema => {
	const normalized = normalizeSchema(schema)
	if (!normalized) return ""
	const text = JSON.stringify(normalized.items)
	let hash = 5381
	for (let i = 0; i < text.length; i++) hash = ((hash * 33) ^ text.charCodeAt(i)) >>> 0
	return `${normalized.items.length}-${hash.toString(36)}`
}

// ── Le frecce ──
// Una freccia va da `a` a `b`; `bend` la piega: 0 è dritta, e col segno dice
// da che parte. Si tiene in proporzione e non come un punto fisso, così
// spostando una punta la curva resta la stessa curva.

export const arrowControl = arrow => {
	const mx = (arrow.a.x + arrow.b.x) / 2
	const my = (arrow.a.y + arrow.b.y) / 2
	const dx = arrow.b.x - arrow.a.x
	const dy = arrow.b.y - arrow.a.y
	const bend = arrow.bend || 0
	return { x: mx - dy * bend, y: my + dx * bend }
}

// Il punto della curva a `t` (da 0, la partenza, a 1, l'arrivo)
export const arrowPoint = (arrow, t) => {
	const c = arrowControl(arrow)
	const m = 1 - t
	return {
		x: m * m * arrow.a.x + 2 * m * t * c.x + t * t * arrow.b.x,
		y: m * m * arrow.a.y + 2 * m * t * c.y + t * t * arrow.b.y,
	}
}

// La piega che fa passare la curva da `p` a metà strada. La metà della
// curva si scosta dalla freccia la metà del punto di controllo: per questo
// il due. Quasi dritta è dritta, così si raddrizza a mano senza fatica.
export const bendThrough = (arrow, p) => {
	const mx = (arrow.a.x + arrow.b.x) / 2
	const my = (arrow.a.y + arrow.b.y) / 2
	const dx = arrow.b.x - arrow.a.x
	const dy = arrow.b.y - arrow.a.y
	const length2 = dx * dx + dy * dy
	if (length2 === 0) return 0
	const bend = (2 * ((p.x - mx) * -dy + (p.y - my) * dx)) / length2
	return Math.abs(bend) < 0.06 ? 0 : round(clamp(bend, -MAX_BEND, MAX_BEND))
}

export const arrowLength = arrow => Math.hypot(arrow.b.x - arrow.a.x, arrow.b.y - arrow.a.y)

// ── Le due viste ──
// Ognuna sa portare un punto del campo sullo schermo (`toScreen`, con `k`,
// quanto è grande lì una cosa alta) e tornare indietro (`toCourt`), per il
// dito che tocca.

// Dall'alto: il campo è un rettangolo, tutto alla stessa scala
export function topView(width, maxHeight) {
	const pad = 6
	const scale = Math.min(
		(width - 2 * pad) / FIELD.width,
		(maxHeight - 2 * pad) / FIELD.length,
	)
	const left = (width - FIELD.width * scale) / 2
	const top = pad
	return {
		mode: "top",
		width,
		height: FIELD.length * scale + 2 * pad,
		toScreen: (x, y) => ({
			X: left + (x + FIELD.width / 2) * scale,
			Y: top + (y + FIELD.length / 2) * scale,
			k: 0.8,
		}),
		toCourt: (X, Y) => ({
			x: (X - left) / scale - FIELD.width / 2,
			y: (Y - top) / scale - FIELD.length / 2,
		}),
	}
}

// In prospettiva, come le schede: una prospettiva vera, con la profondità
// `t` che va da 0 (il bordo di sotto) a 1 (quello in fondo), e il fondo
// largo quanto i tre quarti del davanti
const DEPTH = 0.39
export function perspectiveView(width, height) {
	const far = 1 / (1 + DEPTH)
	const pad = 4
	const usable = height - 2 * pad
	const bottom = height - pad
	return {
		mode: "persp",
		width,
		height,
		toScreen(x, y) {
			const u = (x + FIELD.width / 2) / FIELD.width
			const t = 1 - (y + FIELD.length / 2) / FIELD.length
			const s = 1 / (1 + DEPTH * t)
			return {
				X: width / 2 + (u - 0.5) * width * s,
				Y: bottom - (usable * (1 - s)) / (1 - far),
				k: s,
			}
		},
		toCourt(X, Y) {
			const s = clamp(1 - ((bottom - Y) / usable) * (1 - far), far * 0.9, 1.1)
			const t = (1 / s - 1) / DEPTH
			const u = 0.5 + (X - width / 2) / (width * s)
			return { x: u * FIELD.width - FIELD.width / 2, y: (1 - t) * FIELD.length - FIELD.length / 2 }
		},
	}
}

// Le misure di sempre: lo schema si disegna su questa larghezza, e l'SVG
// lo scala a quella che c'è
export const SCHEMA_WIDTH = 358
export const PERSPECTIVE_HEIGHT = 400

export const schemaView = (mode, maxHeight = 560) =>
	mode === "top"
		? topView(SCHEMA_WIDTH, maxHeight)
		: perspectiveView(SCHEMA_WIDTH, PERSPECTIVE_HEIGHT)

// ── Comporre ──

// Il colore di una sagoma nuova: il primo che non c'è ancora in campo
export const nextPlayerColor = items => {
	const used = new Set(items.filter(item => item.type === "player").map(item => item.color))
	return COLOR_IDS.find(color => !used.has(color)) ?? COLOR_IDS[items.length % COLOR_IDS.length]
}

// Il numero dopo l'ultimo
export const nextNumber = items =>
	Math.min(
		MAX_NUMBER,
		items.filter(item => item.type === "number").reduce((max, item) => Math.max(max, item.n), 0) + 1,
	)

// Dove compare un pezzo nuovo: un posto sensato per quel pezzo, il primo
// libero, così due pezzi aggiunti di fila non finiscono uno sopra l'altro
const SPOTS = {
	player: [[0, 9], [-3, 9], [3, 9], [0, -9], [-3, -9], [3, -9], [0, 3], [0, -3]],
	coach: [[7, -3], [-7, 3], [7, 3], [-7, -3]],
	cone: [[-2, 6.4], [2, 6.4], [-2, -6.4], [2, -6.4], [0, 0.9]],
	number: [[-1.5, -4], [1.5, -4], [-1.5, 4], [1.5, 4]],
}

export const newPiece = (items, type) => {
	if (type === "zone")
		return { type: "zone", x1: -COURT.singles, y1: -COURT.baseline, x2: -1.4, y2: -8.4 }
	const isFree = ([x, y]) =>
		!items.some(item => isNumber(item.x) && Math.hypot(item.x - x, item.y - y) < 1.4)
	const [x, y] = SPOTS[type].find(isFree) ?? SPOTS[type][0]
	if (type === "player")
		return { type, x, y, color: nextPlayerColor(items), ghost: false, num: 0 }
	if (type === "number") return { type, x, y, n: nextNumber(items) }
	if (type === "cone") return { type, x, y, tall: false }
	return { type, x, y }
}

// I giocatori di uno schema, quelli veri: l'ombra è uno di loro in un altro
// momento, e non si conta due volte
export const schemaPlayers = schema =>
	(normalizeSchema(schema)?.items ?? []).filter(item => item.type === "player" && !item.ghost)

// ── L'aggancio ──
// Trascinando un pezzo o la punta di una freccia, quando arriva in fila con
// un altro pezzo, con la punta di un'altra freccia o con una riga del campo
// (il fondo, il servizio, il centro, la rete, i corridoi) ci si attacca:
// basta avvicinarsi, e la fila è dritta davvero. Sotto quella distanza, in
// metri, si aggancia; sopra il pezzo va dove va il dito.
export const SNAP_DISTANCE = 0.3

const COURT_XS = [-COURT.doubles, -COURT.singles, 0, COURT.singles, COURT.doubles]
const COURT_YS = [-COURT.baseline, -COURT.service, 0, COURT.service, COURT.baseline]

// A cosa si aggancia quello che si sposta: tutto tranne lui (`exceptID`)
export const snapTargets = (items, exceptID) =>
	items
		.filter(item => item.id !== exceptID)
		.flatMap(item => (item.type === "arrow" ? [item.a, item.b] : isNumber(item.x) ? [{ x: item.x, y: item.y }] : []))

// Il punto agganciato, le file su cui sta (`guides.x`, `guides.y`, o `null`)
// e con chi è in fila (`with`), per disegnarle mentre si trascina
export const snapPoint = (point, targets, distance = SNAP_DISTANCE) => {
	const nearest = (value, candidates) =>
		candidates.reduce((best, candidate) => {
			const gap = Math.abs(candidate - value)
			return gap <= distance && (best === null || gap < Math.abs(best - value)) ? candidate : best
		}, null)
	const x = nearest(point.x, [...COURT_XS, ...targets.map(target => target.x)])
	const y = nearest(point.y, [...COURT_YS, ...targets.map(target => target.y)])
	return {
		x: x ?? point.x,
		y: y ?? point.y,
		guides: { x, y },
		with: targets.filter(target => (x !== null && target.x === x) || (y !== null && target.y === y)),
	}
}

// ── Duplica ──
// La copia di un pezzo o di una freccia: un metro più in là (a sinistra,
// se a destra non c'è posto). Un giocatore prende il colore dopo e un
// numero il numero dopo, come quelli nuovi; l'ombra e il numero della
// sagoma no, perché dicono chi è quello di prima.
export const duplicateItem = (items, item) => {
	const xs = item.type === "arrow" ? [item.a.x, item.b.x] : item.type === "zone" ? [item.x1, item.x2] : [item.x]
	const dx = Math.max(...xs) + 1 <= FIELD.width / 2 - 0.3 ? 1 : -1
	const move = p => keepOnField({ x: p.x + dx, y: p.y })
	if (item.type === "arrow") return { ...item, a: move(item.a), b: move(item.b) }
	if (item.type === "zone") {
		const from = move({ x: item.x1, y: item.y1 })
		const to = move({ x: item.x2, y: item.y2 })
		return { ...item, x1: from.x, y1: from.y, x2: to.x, y2: to.y }
	}
	const copy = { ...item, ...move(item) }
	if (item.type === "player") return { ...copy, color: nextPlayerColor(items), ghost: false, num: 0 }
	if (item.type === "number") return { ...copy, n: nextNumber(items) }
	return copy
}
